import * as React from 'react';
import { cn } from '../../lib/cn';
import { LiveDuration } from './activity-indicator';

/**
 * ProcessDisclosure — a collapsible, *small & subtle* disclosure that streams a
 * single deep-agent activity chunk (reasoning / tool-args / step body) live, so
 * the wait feels alive. The "Thinking ⌄" pattern of modern chat agents, sized
 * for the muted process zone — set-mate of ActivityTrace.
 *
 * Lifecycle:
 *  - active (streaming): header spinner + label + chevron; while collapsed a
 *    peek of the last N lines fades in below (mask). Expanded → the full chunk
 *    body scrolls (max-height, auto-stuck to bottom, muted, args in mono) with a
 *    blinking caret at the tail.
 *  - done: collapses to our done-trace tone (muted ✓ + label · duration), with
 *    the chevron kept so the reasoning can be re-opened. Expanded → static body.
 *
 * Token-only; reuses the ActivityIndicator/ActivityTrace `--color-status-*` /
 * `--status-*` families (new: `--status-body-max-h`, `--status-chevron-size`).
 * `mode="auto"` uses a @container query to switch full↔panel; motion is
 * neutralized globally by base.css reduced-motion.
 */

export type ProcessDisclosureKind = 'reasoning' | 'args' | 'step';

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

interface KindMeta {
  label: string;
  glyph: React.ReactNode;
  mono: boolean;
}

/** kind → default label + leading glyph (Lucide paths; matches ActivityIndicator). */
export const PROCESS_KIND: Record<ProcessDisclosureKind, KindMeta> = {
  reasoning: {
    label: '생각하는 중',
    glyph: (
      <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
    ),
    mono: false,
  },
  args: {
    label: '도구 인자 구성',
    glyph: (
      <path d="M14.7 6.3a4 4 0 0 0-5.2 5.2L3 18l3 3 6.5-6.5a4 4 0 0 0 5.2-5.2l-2.9 2.9-2.1-2.1z" />
    ),
    mono: true,
  },
  step: {
    label: '단계 진행',
    glyph: (
      <>
        <path d="M8 6h13" />
        <path d="M8 12h13" />
        <path d="M8 18h13" />
        <path d="M3 6h.01" />
        <path d="M3 12h.01" />
        <path d="M3 18h.01" />
      </>
    ),
    mono: false,
  },
};

/** Format a duration for the `done` header. `value` = seconds; unit "s" or "초". */
function formatDuration(value?: number | string | null, unit = 's'): string {
  if (value == null || value === '') return '';
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(n)) return String(value);
  const txt = n >= 10 ? String(Math.round(n)) : n.toFixed(1);
  return unit === '초' ? `${txt}초` : txt + (unit || 's');
}

function lastLines(text: string, n: number): string[] {
  return String(text || '')
    .split('\n')
    .filter((l) => l.trim() !== '')
    .slice(-n);
}

/**
 * One collapsed-peek line. `tail` (mono/args, which streams as one long line)
 * horizontally follows the newest characters — keeps scrollLeft pinned to the
 * end so the latest streamed content stays in view instead of the stale head.
 * Otherwise it truncates with a trailing ellipsis (multi-line reasoning peek).
 */
function PeekLine({ text, tail }: { text: string; tail?: boolean }) {
  const ref = React.useRef<HTMLSpanElement>(null);
  React.useEffect(() => {
    if (tail && ref.current) ref.current.scrollLeft = ref.current.scrollWidth;
  }, [text, tail]);
  return (
    <span
      ref={ref}
      className={cn(
        'block overflow-hidden whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        !tail && 'text-ellipsis',
      )}
    >
      {text}
    </span>
  );
}

const ICON_SIZE = 'size-[var(--status-icon-size)]';

function Spinner() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cn(ICON_SIZE, 'block animate-[lb-spin_0.8s_linear_infinite] text-[var(--color-status-icon)]')}
    >
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
      <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function KindGlyph({ kind }: { kind: ProcessDisclosureKind }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...STROKE}
      className={cn(ICON_SIZE, 'block text-[var(--color-status-icon)]')}
    >
      {(PROCESS_KIND[kind] ?? PROCESS_KIND.reasoning).glyph}
    </svg>
  );
}

function DoneCheck() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...STROKE}
      className={cn(ICON_SIZE, 'block text-[var(--color-status-done-icon)]')}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...STROKE}
      className={cn(
        'block size-[var(--status-chevron-size)] flex-none text-[var(--color-status-done-fg)]',
        'transition-transform duration-[var(--duration-base)] ease-[var(--ease-out)]',
        open && 'rotate-180',
      )}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export interface ProcessDisclosureProps {
  /** Header label. Defaults to a kind-based Korean label. */
  label?: string;
  /** Chunk type — sets the default label, glyph, and body font (`args` → mono). */
  kind?: ProcessDisclosureKind;
  /** The streamed body text (multi-line). Grows live; last N lines peek while collapsed. */
  chunks?: string;
  /** `active` = streaming (spinner + peek + caret); `done` = collapsed done trace (✓ + duration). */
  status?: 'active' | 'done';
  /** Elapsed seconds — shown as `· 2.4s` on the done header (mono). */
  duration?: number | string;
  /** ms epoch the process started — while `active`, a live `· 2.4s` timer ticks up. */
  startedAt?: number;
  /** Duration unit — `"s"` or `"초"` (per preset). Default `"s"`. */
  unit?: string;
  /** Start expanded. Default `false`. */
  defaultOpen?: boolean;
  /** `full` · `panel` (narrow) · `auto` (@container-adaptive). Default `auto`. */
  mode?: 'full' | 'panel' | 'auto';
  /** How many trailing lines to peek while collapsed + active. Default `2`. */
  peekLines?: number;
  className?: string;
}

/**
 * Small, subtle collapsible disclosure that streams one deep-agent chunk live —
 * the "Thinking ⌄" pattern for the muted process zone (set-mate of ActivityTrace).
 */
export function ProcessDisclosure({
  label,
  kind = 'reasoning',
  chunks = '',
  status = 'active',
  duration,
  startedAt,
  unit = 's',
  defaultOpen = false,
  mode = 'auto',
  peekLines = 2,
  className,
}: ProcessDisclosureProps) {
  const isDone = status === 'done';
  const meta = PROCESS_KIND[kind] ?? PROCESS_KIND.reasoning;
  const [open, setOpen] = React.useState(defaultOpen);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  // Follow the live stream ONLY while the user is pinned to the bottom. Once they
  // scroll up we stop auto-scrolling so they can read earlier content freely
  // (fixes: "auto-scrolls down, can't scroll up"). Scroll back down to re-follow.
  const pinnedRef = React.useRef(true);

  const handleScroll = React.useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  }, []);

  // (Re)opening the fold starts pinned to the bottom.
  React.useEffect(() => {
    if (open) pinnedRef.current = true;
  }, [open]);

  // Keep the body stuck to the bottom while streaming — but only if the user
  // hasn't scrolled up (pinned). Manual scroll-up unpins and is respected.
  React.useEffect(() => {
    if (open && !isDone && pinnedRef.current && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [chunks, open, isDone]);

  const text = label ?? meta.label;
  const durText = isDone ? formatDuration(duration, unit) : '';
  const peek = lastLines(chunks, peekLines);
  const hasBody = String(chunks || '').trim() !== '';

  // muted body zone (shared by peek + full body) hangs off a left rail — ties
  // the disclosure into the ActivityTrace "process" zone.
  const rail = cn(
    'ml-[var(--status-rail-indent)] border-l-[1.5px] border-[var(--color-status-rail)] pl-[var(--status-rail-gap)]',
    'text-[length:var(--text-xs)] leading-[var(--leading-normal)] text-[var(--color-status-done-fg)]',
    'whitespace-pre-wrap [overflow-wrap:anywhere]',
    meta.mono ? 'font-mono' : 'font-sans',
  );

  return (
    <div
      className={cn('flex min-w-0 flex-col gap-[var(--space-1)]', mode === 'auto' && '@container', className)}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex w-full min-w-0 cursor-pointer items-center gap-[var(--status-gap)] border-none bg-transparent',
          'rounded-[var(--radius-sm)] py-[var(--space-1)] text-left',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
        )}
      >
        <span className="inline-flex flex-none items-center">
          {isDone ? <DoneCheck /> : open ? <KindGlyph kind={kind} /> : <Spinner />}
        </span>
        <span
          className={cn(
            'min-w-0 truncate font-sans text-[length:var(--status-font)] leading-[var(--leading-snug)]',
            isDone ? 'font-normal text-[var(--color-status-done-fg)]' : 'font-medium text-[var(--color-status-fg)]',
          )}
        >
          {text}
        </span>
        {durText ? (
          <span
            aria-hidden="true"
            className="flex-none font-mono text-[length:var(--text-2xs)] tabular-nums text-[var(--color-status-done-fg)]"
          >
            · {durText}
          </span>
        ) : !isDone && startedAt != null ? (
          <LiveDuration startedAt={startedAt} unit={unit} className="text-[length:var(--text-2xs)]" />
        ) : null}
        <span className="flex-1" />
        <Chevron open={open} />
      </button>

      {/* Expanded → full scroll body (auto-stuck bottom while live). */}
      {open && hasBody ? (
        <div
          ref={bodyRef}
          role="region"
          aria-label={text}
          onScroll={handleScroll}
          className={cn(
            rail,
            'max-h-[var(--status-body-max-h)] overflow-y-auto',
            'animate-[lb-status-in_var(--duration-base)_var(--ease-out)]',
            mode === 'panel' && 'max-h-[156px]',
            mode === 'auto' && '@max-[340px]:max-h-[156px]',
          )}
        >
          {chunks}
          {!isDone ? (
            <span
              aria-hidden="true"
              className="inline-block w-[0.5em] animate-[lb-blink_1s_step-end_infinite] text-[var(--color-status-icon)]"
            >
              ▍
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Collapsed + active → faded peek that follows the newest streamed content:
          mono/args (one long line) scrolls to its tail with a left fade; multi-line
          reasoning shows the last lines with a top fade. */}
      {!open && !isDone && peek.length > 0 ? (
        <div
          aria-hidden="true"
          className={cn(
            rail,
            'flex max-h-[3.2em] flex-col gap-0.5 overflow-hidden',
            meta.mono
              ? '[mask-image:linear-gradient(to_right,transparent,#000_12%)] [-webkit-mask-image:linear-gradient(to_right,transparent,#000_12%)]'
              : '[mask-image:linear-gradient(to_bottom,transparent,#000_55%)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,#000_55%)]',
          )}
        >
          {peek.map((l, i) => (
            <PeekLine key={i} text={l} tail={meta.mono} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
